import React from "react";
import { BedDouble, Bath, MapPin, ExternalLink, Home as HomeIcon, Phone } from "lucide-react";
import PageBanner from "../components/PageBanner";
import Reveal from "../components/Reveal";
import Seo from "../components/Seo";
import { CURRENT_LISTINGS, CONTACT } from "../mock";

const ZILLOW_PROFILE = "https://www.zillow.com/profile/rogerminner#listings-and-sales";

const ListingCard = ({ home }) => (
  <Reveal>
    <a
      href={home.url}
      target="_blank"
      rel="noopener noreferrer"
      className="property-card block rounded-sm overflow-hidden shadow-[0_18px_45px_-30px_rgba(58,55,54,0.4)] h-full flex flex-col group"
    >
      <div className="relative overflow-hidden h-60">
        <img src={home.img} alt={home.address} className="card-img w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#3a3736]/70 to-transparent" />
        <span className="absolute top-4 left-4 bg-[#6f5f62] text-white font-sans-el text-xs tracking-[0.15em] uppercase px-3 py-1.5 rounded-sm">
          {home.status}
        </span>
        <p className="absolute bottom-4 left-4 brand-title text-3xl text-white">{home.price}</p>
      </div>
      <div className="p-6 flex flex-col flex-1">
        <h3 className="font-serif-el text-xl text-[#3a3736]">{home.address}</h3>
        <p className="flex items-center gap-1.5 font-sans-el text-sm text-[#9a878b] mt-1">
          <MapPin size={14} /> {home.city}
        </p>
        <div className="flex items-center gap-5 mt-4 text-[#4a4644]">
          <span className="flex items-center gap-1.5 font-sans-el text-sm"><BedDouble size={16} className="text-[#b6a4a7]" /> {home.beds} Beds</span>
          <span className="flex items-center gap-1.5 font-sans-el text-sm"><Bath size={16} className="text-[#b6a4a7]" /> {home.baths} Baths</span>
        </div>
        <div className="flex items-center justify-between mt-5 pt-4 border-t border-[#eae2d9]">
          <p className="font-sans-el text-xs tracking-[0.2em] uppercase text-[#6f5f62]">
            {home.represented}
          </p>
          <span className="flex items-center gap-1.5 font-sans-el text-xs tracking-[0.12em] uppercase text-[#9a878b] group-hover:text-[#6f5f62] transition-colors">
            View on Zillow <ExternalLink size={13} />
          </span>
        </div>
      </div>
    </a>
  </Reveal>
);

const EmptyState = () => (
  <div className="max-w-[720px] mx-auto text-center">
    <Reveal>
      <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#efe4e5] mb-8">
        <HomeIcon size={34} className="text-[#6f5f62]" />
      </div>
      <h2 className="section-title text-3xl md:text-4xl text-[#3a3736]">
        No Active Listings Right Now
      </h2>
      <div className="divider-ornament mt-6 mb-8">
        <span className="line" /><span className="dot" /><span className="line" />
      </div>
      <p className="font-serif-el text-2xl md:text-3xl leading-relaxed text-[#4a4644]">
        Please check back soon for updated new listings!
      </p>
      <a
        href={ZILLOW_PROFILE}
        target="_blank"
        rel="noopener noreferrer"
        className="btn-mauve inline-flex items-center gap-2 mt-10 px-8 py-4 rounded-sm"
      >
        Check Our Zillow For The Latest <ExternalLink size={16} />
      </a>
    </Reveal>
  </div>
);

const CurrentListings = () => {
  const hasListings = CURRENT_LISTINGS.length > 0;

  return (
    <div>
      <Seo
        title="Current Listings | Roger Minner, Kennett Square PA Realtor"
        description="Browse current homes for sale in Kennett Square, West Chester, Downingtown, and Chadds Ford, PA with Roger Minner. Listings updated across Chester County."
      />
      <PageBanner
        eyebrow="Available Now"
        title="Current Listings"
        subtitle="Discover distinctive homes across the Greater Chester and Delaware Counties."
        image="https://images.unsplash.com/photo-1594348351674-2a23d0bcf0cd?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80"
      />

      <section className="bg-[#f6f2ed] py-20 md:py-24">
        <div className="max-w-[1300px] mx-auto px-6">
          {hasListings ? (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {CURRENT_LISTINGS.map((home) => (
                  <ListingCard key={home.id} home={home} />
                ))}
              </div>

              <Reveal className="text-center mt-16">
                <p className="font-serif-el text-2xl text-[#3a3736] mb-6">
                  Interested in a private showing?
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <a
                    href={ZILLOW_PROFILE}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-mauve inline-flex items-center gap-2 px-8 py-4 rounded-sm"
                  >
                    Check Our Zillow For The Latest <ExternalLink size={15} />
                  </a>
                  <a
                    href={`tel:${CONTACT.phoneRaw}`}
                    className="inline-flex items-center gap-2 border border-[#6f5f62] text-[#6f5f62] px-8 py-4 rounded-sm font-sans-el tracking-[0.14em] uppercase text-sm hover:bg-[#6f5f62] hover:text-white transition-all duration-300"
                  >
                    <Phone size={16} /> Call Roger: {CONTACT.phone}
                  </a>
                </div>
              </Reveal>
            </>
          ) : (
            <EmptyState />
          )}
        </div>
      </section>
    </div>
  );
};

export default CurrentListings;
